import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Chip, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageLayout, PageHeader, SectionCard, EmptyState, LoadingState, ConfirmDialog } from '../../components/ui';
import {
  fetchSurveys, deleteSurvey, activateSurvey, deactivateSurvey, duplicateSurvey, SURVEY_STATUSES,
} from '../../services/surveysApi';

const statusMeta = (s) => SURVEY_STATUSES.find((x) => x.value === s) || { label: s, color: 'default' };

export default function SurveyListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSurveys({ status: status || undefined, q: q || undefined, per_page: 50 });
      setItems(data?.data || []);
    } catch {
      toast.error('Failed to load surveys.');
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, id, ok) => {
    try {
      await fn(id);
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed.');
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Surveys"
        subtitle="Create, distribute and analyze employee surveys"
        actions={<Button variant="contained" onClick={() => navigate('/admin/surveys/new')}>Create survey</Button>}
      />
      <SectionCard>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          <TextField size="small" label="Search" value={q} onChange={(e) => setQ(e.target.value)} />
          <TextField
            size="small" select label="Status" value={status}
            onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            {SURVEY_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
        </Stack>

        {loading ? <LoadingState /> : items.length === 0 ? (
          <EmptyState title="No surveys yet" description="Create your first survey to get started." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Questions</TableCell>
                <TableCell align="right">Responses</TableCell>
                <TableCell align="right">Targets</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((s) => {
                const meta = statusMeta(s.status);
                return (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.title}</TableCell>
                    <TableCell><Chip size="small" label={meta.label} color={meta.color} /></TableCell>
                    <TableCell align="right">{s.questions_count ?? 0}</TableCell>
                    <TableCell align="right">{s.responses_count ?? 0}</TableCell>
                    <TableCell align="right">{s.targets_count ?? 0}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => navigate(`/admin/surveys/${s.id}/edit`)}>Edit</Button>
                        <Button size="small" onClick={() => navigate(`/admin/surveys/${s.id}/results`)}>Results</Button>
                        {s.status === 'active' ? (
                          <Button size="small" color="warning" onClick={() => act(deactivateSurvey, s.id, 'Deactivated.')}>Deactivate</Button>
                        ) : (s.status === 'draft' || s.status === 'inactive') ? (
                          <Button size="small" color="success" onClick={() => act(activateSurvey, s.id, 'Activated.')}>Activate</Button>
                        ) : null}
                        <Button size="small" onClick={() => act(duplicateSurvey, s.id, 'Duplicated.')}>Duplicate</Button>
                        <Button size="small" color="error" onClick={() => setConfirm({ id: s.id, title: s.title })}>Delete</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <ConfirmDialog
        open={!!confirm}
        title="Archive survey?"
        description={confirm ? `"${confirm.title}" will be archived.` : ''}
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          const target = confirm;
          setConfirm(null);
          await act(deleteSurvey, target.id, 'Survey archived.');
        }}
      />
    </PageLayout>
  );
}
